import { extractCategoriesUnlocalized } from '@design-system/utils/formatAlgoliaHierarchicalCategories'
import { selectItemSchema } from '@integration-layer/schemas/GAEvents/selectItem'

import snakeCase from 'lodash/snakeCase'

type Product = {
  sku: string
  name: string
  brand: string
  color: string
  price: number
  quantity?: number
  available: boolean
  img: string
  discount: number
  coupon?: string
}

/**
 * Represents a product with detailed attributes for Google Analytics tracking.
 *
 * @typedef {Object} Product
 * @property {string} sku - Unique product stock keeping unit identifier.
 * @property {string} name - Name of the product.
 * @property {string} brand - Brand of the product.
 * @property {string} color - Color variant of the product.
 * @property {number} price - Price of the product.
 * @property {number} [quantity] - Optional quantity of the product (defaults to 1).
 * @property {boolean} available - Indicates if the product is available.
 * @property {string} img - URL or path to the product image.
 * @property {number} discount - Discount percentage or amount.
 * @property {string} [coupon] - Optional coupon code applied to the product.
 */

/**
 * Composable for tracking product selection events in Google Analytics.
 * Handles dispatching select_item events with comprehensive product details.
 *
 * @returns {Object} An object containing the dispatchSelectItemEvent method.
 * @property {Function} dispatchSelectItemEvent - Sends a select_item event to Google Analytics.
 *
 * @example
 * const { dispatchSelectItemEvent } = useGASelectItem()
 * dispatchSelectItemEvent(productObject)
 */
export const useGASelectItem = () => {
  const appConfig = useAppConfig()
  const algoliaIndex = appConfig.currentAlgoliaIndex
  const { getCurrencySymbolFromCountryCode } = useCurrency()
  const { country } = useRouteHelper()

  /**
   * Dispatches a Google Analytics select_item event with detailed product information.
   *
   * @param {Product} product - The product being selected.
   * @throws {Error} If the product cannot be retrieved or processed.
   *
   * @description
   * - Fetches additional product details from Algolia
   * - Extracts product categories
   * - Prepares comprehensive event data for Google Analytics
   * - Handles special cases like brand naming and currency
   */
  const dispatchSelectItemEvent = async (product: Product) => {
    try {
      const algoliaObject = await $fetch(
        `/api/getProduct/${algoliaIndex}/${product.sku}`
      )

      const firstAvailableProductSize = computed(() => {
        const availableSize = algoliaObject?.size.find(
          item => (item?.avEcom ?? 0) > 0
        )
        return availableSize ? availableSize.label : ''
      })

      const currency = getCurrencySymbolFromCountryCode(country)

      const { category1, category2, category4 } = extractCategoriesUnlocalized(
        algoliaObject?.hierarchicalCategories || [],
        algoliaObject?.categoryPageId2 || []
      )
      const { pdpPath } = useRouteHelper()

      useGASendEvent(
        selectItemSchema,
        {
          event: 'select_item',
          eventID: '004',
          ecommerce: {
            currency: currency?.code ?? 'EUR',
            item_list_id: '',
            item_list_name: snakeCase(`${algoliaObject.Brand}-${category2}`),
            items: [
              {
                item_id: product.sku,
                item_name: snakeCase(product.name),
                affiliation: 'armani.com',
                coupon: product?.coupon ?? '',
                discount: product.discount,
                index: 1,
                item_brand:
                  product.brand.toLowerCase() === 'ea7'
                    ? 'EA7'
                    : snakeCase(product.brand ?? 'armani group'),
                item_category: snakeCase(category1),
                item_category2: snakeCase(category2),
                item_category4: snakeCase(category4),
                item_list_id: '',
                item_list_name: snakeCase(
                  `${algoliaObject.Brand}-${category2}`
                ),
                item_variant: product.color,
                price: product.price,
                quantity: product.quantity ?? 1,
                item_availability: product.available ? 1 : 0,
                item_size: firstAvailableProductSize.value ?? '',
                item_reservable: 0,
                item_discount: product.discount ? 1 : 0,
                item_mfc_id: algoliaObject.MFC,
                item_img: product.img,
                item_url: useRequestURL().origin + pdpPath(algoliaObject),
              },
            ],
          },
        },
        {
          clearEcommerce: true,
        }
      )
    } catch {
      console.error('product not found')
    }
  }
  return { dispatchSelectItemEvent }
}
